import { Avatar, Button, Card, Select, Space, Table, Tag } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import { PropertyType, PtoItemType, ResponsePagingType, SearchPTOParams } from 'Modules'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ButtonCopy from 'component/button/button-copy'
import LinkAddress from 'component/link/link-address'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { DEFAULT_PAGE_SIZE } from 'constants/pagination'
import {
  MINT_STATUS_CONFIG,
  PTO_TYPE_OPTIONS,
  PTOTypeEnum,
  TokenizeMintStatusEnum,
  TokenizeStatusEnum,
} from 'constants/pto'
import { FEATURE } from 'constants/feature'
import { usePermission } from 'hooks/use-permission'
import useSearchParams from 'hooks/use-search-params'
import { PAGES } from 'routes'
import { logError } from 'utils/logger'

import ButtonDeployContract from './button-deploy-contract'
import ButtonMintPTO from './button-mint-pto'
import Overview from './overview'

import './style.scss'

const SORT_ORDER_MAP: Record<string, string> = {
  ascend: 'asc',
  descend: 'desc',
}

const PTO = () => {
  const [filteredData, setFilteredData] = useState<PtoItemType[]>([])
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [total, setTotal] = useState(0)
  const [searchValue, setSearchValue] = useState('')

  const navigate = useNavigate()

  const additionalItems = [BREADCRUMBS.PTO]

  const { canCRUD } = usePermission()

  const { searchParams, setSearchParams, initPagination, onChangePagination, onSearch } =
    useSearchParams<SearchPTOParams>()

  const disableButton = useMemo(() => {
    return !canCRUD(FEATURE.PTO)
  }, [canCRUD])

  const pollingRef = useRef<number>()

  const getData = useCallback(async (params?: SearchPTOParams) => {
    try {
      const res = await AppAdmin.getListPTO(params)

      if (res?.data.success) {
        const { results, totalResults } = res.data?.data || ({} as ResponsePagingType<PtoItemType>)
        setTotal(totalResults || 0)
        setFilteredData(results)
      }
    } catch (error) {
      setTotal(0)
      setFilteredData([])
      logError('Error: ', error)
    }
  }, [])

  const getListPropertyTypes = async () => {
    try {
      const res = await AppAdmin.getListProperty()
      if (res?.data.success) {
        setPropertyTypes(
          res?.data.data.map((item: PropertyType) => ({
            ...item,
            id: Number(item.id),
          }))
        )
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: any,
    sorter: SorterResult<PtoItemType> | SorterResult<PtoItemType>[]
  ) => {
    onChangePagination(pagination)

    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter
    const sort =
      singleSorter.field && singleSorter.order
        ? `${singleSorter.field}:${SORT_ORDER_MAP[singleSorter.order]}`
        : undefined

    setSearchParams((prev) => ({ ...prev, sort }))
  }

  const hasMinting = useMemo(
    () => filteredData.some((item) => item.mintStatus === TokenizeMintStatusEnum.MINTING),
    [filteredData]
  )

  useEffect(() => {
    getData(searchParams)
  }, [searchParams, getData])

  useEffect(() => {
    if (!hasMinting) {
      clearInterval(pollingRef.current)
      return
    }

    pollingRef.current = window.setInterval(() => getData(searchParams), 5000)

    return () => clearInterval(pollingRef.current)
  }, [hasMinting, searchParams, getData])

  useEffect(() => {
    getListPropertyTypes()
  }, [])

  const columns: ColumnsType<PtoItemType> = [
    {
      title: 'Logo',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      render: (logo) => <Avatar src={logo} size={50} />,
    },
    {
      title: 'Name',
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: true,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: true,
      render: (text) => {
        const typeObj = propertyTypes.find((type) => type.id === text)
        return typeObj ? typeObj.name : text
      },
    },
    {
      title: 'PTO Type',
      dataIndex: 'ptoType',
      key: 'ptoType',
      sorter: true,
      render: (text) => {
        if (text === PTOTypeEnum.RENTAL_PTO) return <Tag color="purple">Rental</Tag>
        return <Tag color="blue">PTO</Tag>
      },
    },
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LinkAddress address={text} />
          {text ? <ButtonCopy text={text} /> : null}
        </div>
      ),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) =>
        status === TokenizeStatusEnum.ACTIVE ? (
          <span className="status-success">Active</span>
        ) : (
          <span className="status-fail">Unactive</span>
        ),
    },
    {
      title: 'Mint Status',
      dataIndex: 'mintStatus',
      key: 'mintStatus',
      render: (mintStatus?: TokenizeMintStatusEnum) => {
        if (mintStatus == null) return '-'
        const config = MINT_STATUS_CONFIG[mintStatus]
        if (!config) return <Tag>Unknown</Tag>
        return <Tag color={config.color}>{config.label}</Tag>
      },
    },
    {
      title: 'Pre-minted',
      dataIndex: 'preMintedSupply',
      key: 'preMintedSupply',
      render: (_, record) => {
        const minted = record.preMintedSupply ?? 0
        const total = record.maxNFTSupply ?? 0
        return `${minted} / ${total}`
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          {!record.contractAddress ? (
            <ButtonDeployContract id={record._id} onSuccess={() => getData(searchParams)} />
          ) : null}
          {record.contractAddress && record.mintStatus != null ? (
            <ButtonMintPTO
              id={record._id}
              mintStatus={record.mintStatus}
              onSuccess={() => getData(searchParams)}
            />
          ) : null}
          <Button
            className="button-detail"
            onClick={() => {
              navigate(`${PAGES.PTO}/${record._id}`)
            }}
          >
            View
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="main-section">
      <Breadcrumb additionalItems={additionalItems} />
      <Overview />
      <div className="main-title">PTO Admin</div>
      <div className="table-row">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="p2p-search">
            <input
              type="text"
              placeholder="Search by name or contract address..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSearch({ keyword: searchValue } as SearchPTOParams)
                }
              }}
            />
            <Select
              allowClear
              placeholder="All Types"
              value={searchParams.ptoType}
              onChange={(value) => onSearch({ ptoType: value } as SearchPTOParams)}
              options={PTO_TYPE_OPTIONS}
              style={{ width: 120 }}
            />
            <Button
              type="primary"
              onClick={() => onSearch({ keyword: searchValue } as SearchPTOParams)}
            >
              Search
            </Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button
              disabled={disableButton}
              type="primary"
              onClick={() => {
                navigate(PAGES.PTO_CREATE)
              }}
            >
              Create
            </Button>
          </div>
        </div>
        <Card>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => record._id}
            pagination={total <= DEFAULT_PAGE_SIZE ? false : initPagination(total)}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
          />
        </Card>
      </div>
    </div>
  )
}

export default PTO
